import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trophy, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { starApi } from "@/lib/extensions-api";
import { formatDateTime, formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

interface Form {
  min_own_points: number;
  min_team_points: number;
  own_only_threshold: number;
  max_team_depth: number;
}

export function StarConfigPage() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["admin-star-config"],
    queryFn: starApi.admin.getConfig,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Form>({
    defaultValues: {
      min_own_points: 1000, min_team_points: 1000,
      own_only_threshold: 2000, max_team_depth: -1,
    },
  });

  const publish = useMutation({
    mutationFn: starApi.admin.publishConfig,
    onSuccess: () => {
      toast.success("New Star qualification rule published.");
      qc.invalidateQueries({ queryKey: ["admin-star-config"] });
      reset();
    },
  });

  // 🛠️ NEW: Force Reset Mutation
  const forceReset = useMutation({
    mutationFn: starApi.admin.forceStarPointReset,
    onSuccess: (res) => {
      // 🛠️ FIXED: Added optional chaining 'res?.message' to prevent undefined crashes
      toast.success(res?.message || "All Star Points have been successfully reset to 0.");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-star-offer-detail"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to reset points.");
    }
  });

  // Handler with confirmation prompt
  const handleForceReset = () => {
    const isConfirmed = window.confirm(
      "⚠️ WARNING: Are you sure you want to reset ALL users' Own and Team Star Points to ZERO?\n\nThis will NOT affect lifetime points or active memberships. It only resets the current month's counter used for Pool/Offer targets.\n\nThis action CANNOT be undone!"
    );
    if (isConfirmed) {
      forceReset.mutate();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Star Configuration</h1>
          <p className="text-sm text-surface-500 mt-1">
            Manage Star Member qualification rules and point resets.
          </p>
        </div>

        {/* 🛠️ NEW: Force Point Reset Button */}
        <Button 
          variant="danger" 
          leftIcon={<RotateCcw className="h-4 w-4" />}
          loading={forceReset.isPending}
          onClick={handleForceReset}
          className="shadow-sm"
        >
          Force Point Reset
        </Button>
      </header>

      <Card>
        <CardHeader><CardTitle>Currently Active Rule</CardTitle></CardHeader>
        <CardBody>
          {current.isLoading ? <Skeleton className="h-24" /> : current.data ? (
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Min Own"          value={formatNumber(current.data.min_own_points)} />
              <Field label="Min Team"         value={formatNumber(current.data.min_team_points)} />
              <Field label="Own-Only"         value={formatNumber(current.data.own_only_threshold)} />
              <Field label="Team Depth"
                      value={current.data.max_team_depth === -1
                        ? "Unlimited"
                        : String(current.data.max_team_depth)} />
              <div className="sm:col-span-4 pt-3 border-t border-surface-200 text-xs text-surface-500">
                Version <b>#{current.data.version_number}</b> · effective {formatDateTime(current.data.effective_from)} · by {current.data.created_by}
              </div>
            </div>
          ) : (
            <div className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg p-3">
              No qualification rule active yet.
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Publish New Rule</CardTitle></CardHeader>
        <CardBody>
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 flex gap-2 text-xs text-brand-800 mb-4">
            <Trophy className="h-4 w-4 shrink-0 mt-0.5" />
            Existing Star Members keep their status — this rule applies to
            new qualifications going forward.
          </div>

          <form onSubmit={handleSubmit((v) => publish.mutate({
            ...v,
            max_team_depth: Number(v.max_team_depth),
          }))} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Minimum Own Points"
                type="number"
                min="0"
                {...register("min_own_points", { required: true, valueAsNumber: true, min: 0 })}
              />
              <Input
                label="Minimum Team Points"
                type="number"
                min="0"
                {...register("min_team_points", { required: true, valueAsNumber: true, min: 0 })}
              />
              <Input
                label="Own-Only Threshold"
                type="number"
                min="0"
                hint="Users meeting only this can qualify without team support."
                {...register("own_only_threshold", { required: true, valueAsNumber: true, min: 0 })}
              />
              <Input
                label="Max Team Depth"
                type="number"
                min="-1"
                hint="Use -1 for unlimited generations."
                {...register("max_team_depth", { required: true, valueAsNumber: true, min: -1 })}
              />
            </div>
            <Button type="submit" loading={publish.isPending}>Publish Rule</Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase text-surface-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-surface-900">{value}</div>
    </div>
  );
}